<template>
  <div class="order-detail-container">
    <!-- 页面标题 -->
    <div class="page-header">
      <h2 class="page-title">订单详情</h2>
      <div class="page-actions">
        <div class="order-number-badge" v-if="order.orderSn">
          订单编号: {{ order.orderSn }}
        </div>
        <div class="actions">
          <el-button size="mini" icon="el-icon-arrow-left" @click="$router.back()">返回</el-button>
          <el-button size="mini" icon="el-icon-refresh" @click="refreshPage">刷新</el-button>
        </div>
      </div>
    </div>

    <!-- 主内容区 -->
    <div class="main-content">
      <!-- 左侧订单信息和产品信息 -->
      <div class="left-panel">
        <!-- 订单信息卡片 -->
        <div class="order-detail-wrapper" v-loading="loadingOrder">
          <!-- 订单取消提示 -->
          <el-alert
            v-if="order.orderState === 6"
            title="订单已取消"
            type="error"
            show-icon
            closable

          />

          <!-- 订单状态卡片 -->
          <div class="status-card">
            <div class="status-header">
              <span class="status-title">订单状态</span>
              <el-tag
                :type="getOrderStateType(order.orderState)"
                class="status-tag"
              >
                {{ getOrderStateName(order.orderState) }}
              </el-tag>
            </div>
            <div class="status-info">
              <div class="status-item">
                <span class="status-label">归属人：</span>
                <span class="status-value">{{ order.employee.adminName }}</span>
              </div>
              <div class="status-item">
                <span class="status-label">创建时间：</span>
                <span class="status-value">{{ formatDate(order.createTime) }}</span>
              </div>
            </div>
          </div>

          <!-- 订单信息（合并） -->
          <div class="info-card order-info">
            <div class="card-header">
              <i class="el-icon-document"></i>
              <span>订单信息</span>
            </div>
            <div class="card-body">
              <!-- 基础信息 -->
              <div class="section-subtitle">基础信息</div>
              <div class="info-grid">
                <div class="info-item">
                  <span class="label">客户名称：</span>
                  <span class="value">{{ order.customer.customerName }}</span>
                </div>
                <div class="info-item">
                  <span class="label">手机号：</span>
                  <span class="value">{{ order.customer.phoneNumber }}</span>
                </div>
                <div class="info-item">
                  <span class="label">销售渠道：</span>
                  <span class="value">{{ gettypeNameById(order.customer.typeId) }}</span>
                </div>
                <div class="info-item">
                  <span class="label">客户级别：</span>
                  <span class="value">{{ getlabelNameById(order.customer.labelId) }}</span>
                </div>
                <div class="info-item full">
                  <span class="label">客户来源：</span>
                  <span class="value">{{ getsourceNameById(order.customer.sourceId) }}</span>
                </div>
              </div>

              <div class="divider"></div>

              <!-- 支付信息 -->
              <div class="section-subtitle">支付信息</div>
              <div class="payment-highlight compact">
                <span class="payment-label">订单金额：</span>
                <span class="payment-value">{{ formatCurrency(order.orderAmount) }}</span>
              </div>
              <div class="info-grid">
                <div class="info-item">
                  <span class="label">支付方式：</span>
                  <span class="value">{{ order.payType }}</span>
                </div>
                <div class="info-item">
                  <span class="label">支付状态：</span>
                  <span class="value">
                    <el-tag :type="getPaymentStatusType(order.paymentStatus)" size="small">{{ order.paymentStatus }}</el-tag>
                  </span>
                </div>
              </div>

              <div class="divider"></div>

              <!-- 物流信息 -->
              <div class="section-subtitle">物流信息</div>
              <div class="info-grid">
                <div class="info-item">
                  <span class="label">发货方式：</span>
                  <span class="value">{{ order.logisticsMethods }}</span>
                </div>
                <div class="info-item">
                  <span class="label">快递公司：</span>
                  <span class="value">{{ order.logisticsCompanies }}</span>
                </div>
                <div class="info-item">
                  <span class="label">快递单号：</span>
                  <span class="value">{{ order.trackingNumber }}</span>
                </div>
                <div class="info-item full">
                  <span class="label">收货地址：</span>
                  <span class="value">{{ order.shippingAddress }}</span>
                </div>
              </div>

              <div class="divider"></div>

              <!-- 备注与收款凭证 -->
              <div class="section-subtitle">备注与收款凭证</div>
              <div class="info-grid">
                <div class="info-item full">
                  <span class="label">订单备注：</span>
                  <span class="value">{{ order.notes || '无' }}</span>
                </div>
                <div class="info-item full">
                  <span class="label">物流备注：</span>
                  <span class="value">{{ order.remarks || '无' }}</span>
                </div>
                <div class="info-item full">
                  <span class="label">收款凭证：</span>
                  <div class="receipt-container receipt-row">
                    <div v-if="order.receipt && order.receipt.length">
                      <el-image
                        v-for="(img, index) in order.receipt"
                        :key="index"
                        :src="img"
                        :preview-src-list="order.receipt"
                        class="receipt-img"
                        fit="cover"
                      />
                    </div>
                    <div v-else class="no-image">暂无图片</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 产品信息 -->
        <div class="product-section">
          <div class="section-header">
            <h3 class="section-title">订单产品信息</h3>
            <div class="warehouse-info">
              <el-tag size="small" type="info" effect="plain">
                <i class="el-icon-location" style="margin-right:4px"></i>发货仓库：{{ order.stash || '—' }}
              </el-tag>
              <span class="product-count-badge">共 {{ productList.length || 0 }} 件</span>
            </div>
          </div>

          <el-table
            :data="productList"
            border
            class="product-table"
            highlight-current-row
            v-loading="loadingProducts"
            :empty-text="loadingProducts ? '正在加载产品...' : '暂无产品数据'"
            stripe
            size="small"
          >
            <el-table-column label="产品名称" prop="productName" align="center" min-width="200" />
            <el-table-column label="产品图片" prop="picture" align="center" width="100">
              <template slot-scope="scope">
                <el-image
                  :src="scope.row.picture"
                  class="product-image"
                  :preview-src-list="[scope.row.picture]"
                />
              </template>
            </el-table-column>
            <el-table-column label="产品价格" align="right">
              <template slot-scope="scope">{{ formatCurrency(scope.row.price) }}</template>
            </el-table-column>
            <el-table-column label="售价" align="right">
              <template slot-scope="scope">{{ formatCurrency(scope.row.costPrice) }}</template>
            </el-table-column>
            <el-table-column label="选购数量" prop="orderNum" align="center" />
            <el-table-column label="成交价" align="right">
              <template slot-scope="scope">{{ formatCurrency(scope.row.salePrice) }}</template>
            </el-table-column>
            <el-table-column label="金额" align="right">
              <template slot-scope="scope">{{ formatCurrency(scope.row.orderAmount) }}</template>
            </el-table-column>
          </el-table>
          <div class="table-summary" v-if="productList && productList.length">
            <span>合计数量：<b>{{ totalQuantity }}</b></span>
            <span>合计金额：<b>{{ formatCurrency(totalAmount) }}</b></span>
          </div>
        </div>
      </div>

      <!-- 右侧时间线信息 -->
      <div class="right-panel">
        <OrderTime
          :order-sn="copyOrderSn"
          :show-export="true"
          :order-state="orderState"
        />
      </div>
    </div>
  </div>
</template>


<script>

import OrderTime from "./orderTime.vue";
export default {
  name:'orderDetail',
components:{
    OrderTime
},
  computed: {
    totalQuantity() {
      return this.total(this.productList, 'orderNum');
    },
    totalAmount() {
      return this.total(this.productList, 'orderAmount');
    }
  },
  data() {
    return {
      productId:1,
      drawerVisible: false,
      selectedProductId: null,
      //描述组件
      labelstyle:{
        'width':'17vh',
      },
      contentStyle: {
        width: '25vh', // 设置内容的宽度
      },
      //时间线组件
      orderTimeList:[],
      //还款记录组件
      repayTimeList:[],
      reverse:false,

     // showLargeImage: false,
      order: {
        employee: {},
        customer:{},
        receipt:[],
      },
      showLargeImageIndex: null,
      //订单编号
      copyOrderSn:'',
      orderState:'',
      acquisitionOrders: [],//所有订单

      // 客户级别
      custoLabel: [],
      // 客户销售渠道
      custoType: [],
      //客户来源
      custoSource: [],
      productList: [],
      orderReviewer: [],
      selectedAuditor: null, // 选中的审核人
      auditComment: '', // 审核备注
      // 加载与错误状态
      loadingOrder: false,
      loadingProducts: false,
      errorOrder: '',
      errorProducts: ''
    };

  },
  methods: {
    refreshPage() {
      this.fetchOrderDetail();
    },

    // 合计
    total(arr, key) {
      if (!Array.isArray(arr)) return 0;
      return arr.reduce((sum, item) => sum + (Number(item[key]) || 0), 0);
    },
    // 不再需要打印
    openTrend(productId) {
      this.selectedProductId = productId
      this.drawerVisible = true
    },
    openOrderDetail(orderSn) {
      const routeData = this.$router.resolve({
        name: '订单详情',
        params: { orderSn }
      });
      window.open(routeData.href, '_blank');
    },
    async fetchOrderDetail() {
      const orderSn = this.copyOrderSn; // 获取路由参数或 props
      if (!orderSn) return;
      this.errorOrder = '';
      this.loadingOrder = true;
      try {
        const response = await this.$http.get(`/orderList/${orderSn}`);
        const data = response.data;
        if (data) {
          this.order = {
            ...data,
            receipt: data.receipt ? data.receipt.split(',') : [],
            voucher: data.voucher ? data.voucher.split(',') : []
          };
          this.orderState=this.order.orderState
          console.log('this.orderState',this.orderState)
          if (['欠款', '部分还款','还款完成'].includes(this.order.paymentStatus)) {
            this.getOrderRepaymentTime();
          }
        } else {
          this.order = {};
          this.errorOrder = '订单详情数据为空';
        }
      } catch (error) {
        this.errorOrder = '获取订单详情失败';
        // 控制台保留详细日志
        // eslint-disable-next-line no-console
        console.error('获取订单详情失败:', error);
      } finally {
        this.loadingOrder = false;
      }

      // 获取订单产品
      this.errorProducts = '';
      this.loadingProducts = true;
      try {
        const res = await this.$http.post('/orderProducid/' + orderSn);
        this.productList = Array.isArray(res.data) ? res.data : [];
        if (!Array.isArray(res.data)) {
          this.errorProducts = '产品数据格式不正确';
        }
      } catch (error) {
        this.errorProducts = '获取产品列表失败';
        // eslint-disable-next-line no-console
        console.error('获取订单产品失败:', error);
      } finally {
        this.loadingProducts = false;
      }
    },
    // 复制文本到剪贴板
    async copyToClipboard(text) {
      if (!text) return;
      try {
        if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          const textarea = document.createElement('textarea');
          textarea.value = text;
          textarea.style.position = 'fixed';
          textarea.style.opacity = '0';
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);
        }
        this.$message.success('复制成功');
      } catch (e) {
        this.$message.error('复制失败，请手动复制');
      }
    },
    formatCurrency(value) {
      if (value === null || value === undefined || value === '') return '—';
      const num = Number(value);
      if (Number.isNaN(num)) return String(value);
      return new Intl.NumberFormat('zh-CN', { style: 'currency', currency: 'CNY' }).format(num);
    },
    formatDate(value) {
      if (!value) return '';
      try {
        const d = new Date(value);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const hh = String(d.getHours()).padStart(2, '0');
        const mm = String(d.getMinutes()).padStart(2, '0');
        const ss = String(d.getSeconds()).padStart(2, '0');
        return `${y}-${m}-${day} ${hh}:${mm}:${ss}`;
      } catch (e) {
        return value;
      }
    },
    getOrderRepaymentTime() {
      const orderSn = this.copyOrderSn;
      if (!orderSn) return;
      this.$http.post('/orderRepayment/' + orderSn).then(res => {
        const data = Array.isArray(res.data) ? res.data : [];
        this.repayTimeList = data.map(item => ({
          ...item,
          time: this.formatDate(item.repaymentTime)
        }));
      }).catch(err => {
        // eslint-disable-next-line no-console
        console.error('获取还款记录失败:', err);
      });
    },
    // 获取订单状态类型
    getOrderStateType(state) {
      switch (state) {
        case 0:
        case 1:
        case 3:
        case 7:
          return 'warning';
        case 2:
        case 6:
          return 'danger';
        case 4:
          return 'primary';
        case 5:
          return 'success';
        default:
          return '';
      }
    },
    // 获取订单状态名称
    getOrderStateName(state) {
      switch (state) {
        case 0:
          return '待审核';
        case 1:
          return '待复审';
        case 2:
          return '审核未通过';
        case 3:
          return '待发货';
        case 4:
          return '已发货';
        case 5:
          return '交易成功';
        case 6:
          return '订单取消';
        case 7:
          return '待终审';
        default:
          return '';
      }
    },
    // 获取支付状态类型
    getPaymentStatusType(status) {
      switch (status) {
        case '欠款':
          return 'danger';
        case '部分还款':
          return 'warning';
        case '还款完成':
          return 'success';
        default:
          return 'primary';
      }
    },
    showLargeImage(index) {
      this.showLargeImageIndex = index;
    },
    // //获取订单时间线
    // getOrderTime() {
    //   const orderSn = this.copyOrderSn; // 获取路由参数或 props
    //   this.$http.post("/orderTime/" + orderSn).then(res => {
    //     const data = res.data;
    //     // 确保格式化每个时间项
    //     this.orderTimeList = data.map(item => ({
    //       ...item,
    //       time: this.$formatTimeNew(item.time)
    //     }));
    //   });
    // },
    // getOrderRepaymentTime() {
    //   const orderSn = this.copyOrderSn; // 获取路由参数或 props
    //   this.$http.post("/orderRepayment/" + orderSn).then(res => {
    //     const data = res.data;
    //     // 确保格式化每个时间项
    //     this.repayTimeList = data.map(item => ({
    //       ...item,
    //       time: this.$formatTimeNew(item.repaymentTime)
    //     }));
    //   });
    // },
    //根据内容显示动态的图标
    getColor(item, index) {
      if (index === 0) {
        return "#5EA8F8";
      } else if (
          item.content.includes("订单审核未通过") ||
          item.content.includes("订单取消")
      ) {
        return "#F6665D";
      } else {
        return "#93F9A5";
      }
    },
    getrePayColor(item, index) {
      if (index === 0) {
        return "#5EA8F8"; // 默认颜色，当前项高亮为蓝色
      } else if (item.style.includes("部分还款")) {
        return "#FFD700"; // 蓝色，用于部分还款
      } else if (item.style.includes("欠款")) {
        return "#FF6333"; // 黄色，用于欠款
      } else if (item.style.includes("还款完成")) {
        return "#93F9A5"; // 绿色，用于欠款完成
      } else {
        return "#CCCCCC"; // 默认灰色，适用于其他情况
      }
    },
    getTagType(item, index) {
      if (index === 0) {
        return ""; // 默认类型（无样式）
      } else if (
          item.content.includes("订单审核未通过") ||
          item.content.includes("订单取消")
      ) {
        return "danger"; // 红色类型
      } else {
        return "success"; // 绿色类型
      }
    },
    getreypayTagType(item, index) {
      if (index === 0) {
        return ""; // 默认类型（无样式）
      } else if (item.style.includes("部分还款")) {
        return "warning"; // 蓝色类型
      } else if (item.style.includes("欠款")) {
        return "danger"; // 黄色类型
      } else if (item.style.includes("还款完成")) {
        return "success"; // 绿色类型
      } else {
        return ""; // 默认类型
      }
    },
    //获取客户数据信息
    gettypeNameById(typeId) {
      const category = this.custoType.find(item => item.id === typeId);
      return category ? category.customerType : '';
    },
    getlabelNameById(labelId) {
      const category = this.custoLabel.find(item => item.id === labelId);
      return category ? category.customerLabel : '';
    },
    getsourceNameById(sourceId) {
      const category = this.custoSource.find(item => item.id === sourceId);
      return category ? category.customerSource : '';
    },
    getReviewerNameById(reviewerKey) {
      const reviewer = this.orderReviewer.find(item => item.id === reviewerKey);
      return reviewer ? reviewer.adminName : '';
    },
    //获取类型
    getType() {
      this.$http.get("/customerType").then(res => {
        this.custoType = res.data;
      })
    },
    //获取标签
    getLabel() {
      this.$http.get("/customerLabel").then(res => {
        this.custoLabel = res.data;
      })
    },
    //获取来源
    getSerouce() {
      this.$http.get("/customerSource").then(res => {
        this.custoSource = res.data;
      })
    },
    },
  mounted() {
  const orderSn = this.$route.params.orderSn; // 从路由参数获取订单编号
  if (!orderSn) {
      // 如果订单编号不存在则跳转到我的订单
      this.$router.push({name: '我的订单列表'});
    }else{
    this.copyOrderSn=orderSn;
    this.fetchOrderDetail();
    this.getSerouce();
    this.getType();
    this.getLabel();
    // this.getOrderTime();

  }
}
};
</script>

<style scoped>
/* 全局样式 */
.order-detail-container {
  min-height: 100vh;
  background-color: transparent;
  padding: 20px;
}

/* 页面标题 */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding: 20px;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.page-title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #303133;
}

.order-number-badge {
  background-color: #e6f7ff;
  color: #1890ff;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
}

.page-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.actions {
  display: flex;
  gap: 8px;
}

/* 主内容区 */
.main-content {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.left-panel {
  flex: 1;
  min-width: 600px;
}

.right-panel {
  flex: 0 0 400px;
  min-width: 300px;
}

/* 订单详情包装器 */
.order-detail-wrapper {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 24px;
}

/* 状态卡片 */

.status-card {
  background: #fff;
  color: #303133;
  border-radius: 12px;
  padding: 20px;
  border-left: 4px solid #409eff;
  box-shadow: 0 1px 6px rgba(0,0,0,0.06);
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.status-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(0,0,0,0.08);
}

.status-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.status-title {
  font-size: 16px;
  opacity: 0.9;
}

.status-tag {
  font-size: 16px;
  font-weight: 600;

}

.status-info {
  display: flex;
  gap: 32px;
}

.status-item {
  display: flex;
  align-items: center;
}

.status-label {
  opacity: 0.8;
  margin-right: 8px;
  font-size: 14px;
}

.status-value {
  font-size: 15px;
  font-weight: 500;
}

/* 信息卡片 */
.info-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 6px rgba(0,0,0,0.06);
  overflow: hidden;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.info-card:hover {
  box-shadow: 0 4px 14px rgba(0,0,0,0.08);
  transform: translateY(-1px);
}

.card-header {
  background: #f8f9fa;
  padding: 16px 20px;
  font-size: 16px;
  font-weight: 600;
  border-bottom: 1px solid #ebeef5;
  color: #303133;
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-header i {
  color: #409eff;
}

.card-body {
  padding: 20px;
}

.order-info .section-subtitle {
  margin: 4px 0 10px 0;
  font-size: 14px;
  color: #606266;
  font-weight: 600;
}

.order-info .divider {
  height: 1px;
  background: #f0f2f5;
  margin: 12px 0;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 16px;
}

.info-grid .info-item.full {
  grid-column: 1 / -1;
}

/* 信息行和项 */
.info-row {
  display: flex;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 16px;
}

.info-row:last-child {
  margin-bottom: 0;
}

.info-item {
  flex: 1;
  min-width: 200px;
  display: flex;
  align-items: center;
}

.info-item.full {
  flex: 100%;
  align-items: flex-start;
}

.label {
  font-weight: 500;
  color: #606266;
  margin-right: 12px;
  white-space: nowrap;
  min-width: 80px;
}

.value {
  color: #303133;
  word-break: break-all;
  flex: 1;
}

/* 可复制文本 */
.copyable-text {
  cursor: pointer;
  color: #409eff;
  position: relative;
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.copyable-text:hover {
  background-color: #ecf5ff;
}

/* 支付信息高亮 */
.payment-highlight {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding: 16px;
  background-color: #f0f9ff;
  border-radius: 8px;
  border-left: 4px solid #1890ff;
}

.payment-label {
  font-weight: 600;
  color: #606266;
  margin-right: 12px;
}

.payment-value {
  font-size: 20px;
  font-weight: 700;
  color: #f56c6c;
}

.payment-value--gradient {
  background: linear-gradient(90deg, #ff7a45, #ff4d4f);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* 凭证图片 */
.receipt-container {
  margin-top: 8px;
}

.receipt-img {
  width: 100px;
  height: 100px;
  margin-right: 12px;
  margin-bottom: 12px;
  border-radius: 6px;
  border: 1px solid #ebeef5;
  transition: all 0.3s ease;
  cursor: pointer;
}

.receipt-img:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.receipt-row {
  display: flex;
  align-items: center;
  gap: 12px;
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  padding-bottom: 4px;
}

.no-image {
  color: #909399;
  font-size: 14px;
  padding: 16px 0;
}

/* 产品部分 */
.product-section {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 6px rgba(0,0,0,0.06);
}

.section-header {
  margin-bottom: 20px;
}

.section-title {
  margin: 0 0 12px 0;
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.warehouse-info {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #409eff;
  font-weight: 500;
  font-size: 14px;
}

.product-count-badge {
  margin-left: auto;
  background: #f5f7fa;
  color: #606266;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
}

/* 产品表格 */
.product-table {
  border-radius: 8px;
  overflow: hidden;
}

.product-table >>> .el-table__header th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #303133;
  border-bottom: 2px solid #ebeef5;
}

.product-table >>> .el-table__body tr:hover {
  background-color: #fafafa;
}

.product-image {
  width: 60px;
  height: 60px;
  border-radius: 4px;
  transition: transform 0.3s ease;
}

.product-image:hover {
  transform: scale(1.1);
}

.table-summary {
  display: flex;
  justify-content: flex-end;
  gap: 20px;
  margin-top: 10px;
  color: #606266;
}

.table-summary b {
  color: #303133;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .main-content {
    flex-direction: column;
  }

  .right-panel {
    flex: 1;
    min-width: auto;
  }
}

@media (max-width: 768px) {
  .order-detail-container {
    padding: 10px;
  }

  .page-header {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }

  .status-info {
    flex-direction: column;
    gap: 12px;
  }

  .info-item {
    min-width: 100%;
  }

  .left-panel {
    min-width: auto;
  }
}

/* 滚动条样式 */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>



// WEBPACK FOOTER //
// orderDetail.vue?012a60d7